/**
 * Pagination Component
 * Reusable pagination controls with page size selector
 */
import React from "react";
import PropTypes from "prop-types";
import "./Pagination.scss";

export const Pagination = ({
  currentPage = 1,
  totalItems = 0,
  itemsPerPage = 25,
  onPageChange,
  onItemsPerPageChange,
  itemsPerPageOptions = [25, 50, 100],
  className = "",
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const handlePrevious = () => {
    if (currentPage > 1 && onPageChange) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages && onPageChange) {
      onPageChange(currentPage + 1);
    }
  };

  const handleFirst = () => {
    if (currentPage > 1 && onPageChange) {
      onPageChange(1);
    }
  };

  const handleLast = () => {
    if (currentPage < totalPages && onPageChange) {
      onPageChange(totalPages);
    }
  };

  if (totalItems === 0) {
    return null;
  }

  return (
    <div className={`pagination ${className}`}>
      <div className="pagination__info">
        <span>
          Showing {startItem}-{endItem} of {totalItems}
        </span>
      </div>

      <div className="pagination__controls">
        <select
          className="pagination__per-page"
          value={itemsPerPage}
          onChange={(e) =>
            onItemsPerPageChange && onItemsPerPageChange(Number(e.target.value))
          }
        >
          {itemsPerPageOptions.map((option) => (
            <option key={option} value={option}>
              {option} per page
            </option>
          ))}
        </select>

        <div className="pagination__buttons">
          <button
            className="pagination__button"
            onClick={handleFirst}
            disabled={currentPage === 1}
          >
            First
          </button>
          <button
            className="pagination__button"
            onClick={handlePrevious}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span className="pagination__page">
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="pagination__button"
            onClick={handleNext}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
          <button
            className="pagination__button"
            onClick={handleLast}
            disabled={currentPage === totalPages}
          >
            Last
          </button>
        </div>
      </div>
    </div>
  );
};

Pagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalItems: PropTypes.number.isRequired,
  itemsPerPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onItemsPerPageChange: PropTypes.func,
  itemsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  className: PropTypes.string,
};
